// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

// feature-components
import ActiveHoursTooltip from '@/features/energyxplorer/components/card/ActiveHoursTooltip';

// feature-hooks
import { useEnergyGroupOpeningHours } from '@/features/energyxplorer/hooks/useEnergyGroupOpeningHours';
import useUrlFilters from '@/features/energyxplorer/hooks/useUrlFilters';

const fallbackDaysOfWeek = {
  monday: [{ start: 7, end: 17 }],
  tuesday: [{ start: 7, end: 17 }],
  wednesday: [{ start: 7, end: 17 }],
  thursday: [{ start: 7, end: 17 }],
  friday: [{ start: 7, end: 17 }],
  saturday: [],
  sunday: [],
};

const DAYS = [
  { key: 'monday', label: 'M' },
  { key: 'tuesday', label: 'T' },
  { key: 'wednesday', label: 'O' },
  { key: 'thursday', label: 'T' },
  { key: 'friday', label: 'F' },
  { key: 'saturday', label: 'L' },
  { key: 'sunday', label: 'S' },
];

const DayPills = ({ daysOfWeek, invert = false, size = 16 }) => {
  let days = daysOfWeek;
  let dimmed = false;
  if (!days) {
    days = fallbackDaysOfWeek;
    dimmed = true;
  }
  const { t } = useTranslation('energy-analysis');

  return (
    <Tooltip
      title={
        dimmed ? (
          <Typography variant="body2">
            {t('no_consumption_pattern_found')}
          </Typography>
        ) : null
      }
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        {DAYS.map(({ key, label }) => {
          const on = invert
            ? !(days?.[key]?.length > 0)
            : days?.[key]?.length > 0;
          return (
            <Box
              key={key}
              component="span"
              sx={{
                width: size,
                height: size,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 0,
                fontWeight: 'bold',
                fontSize: '0.60rem',
                p: 0,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: on ? 'grey.600' : 'transparent',
                color: on ? 'common.white' : 'text.primary',
                opacity: dimmed ? 0.5 : 1,
                boxSizing: 'border-box',
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>
    </Tooltip>
  );
};

const EnergyAnalyzerFilterInput = ({ scope = 'analysis' }) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  const { openingHours } = useEnergyGroupOpeningHours();

  const { filters, applyFilters } = useUrlFilters({
    activePeriodFilter: undefined,
    activeHolidaysFilter: undefined,
  });

  const activePeriodFilter = filters.activePeriodFilter; // { from: string, to: string } | undefined
  const daysFilter = filters.activeDaysFilter; // true | false | undefined
  const holidaysFilter = filters.activeHolidaysFilter; // true | false | undefined

  const setActivePeriodFilter = (val) =>
    applyFilters({ activePeriodFilter: val });
  const setDaysFilter = (val) => applyFilters({ activeDaysFilter: val });
  const setHolidaysFilter = (val) =>
    applyFilters({ activeHolidaysFilter: val });

  const renderToggleGroup = (groupLabel, value, setter, options) => (
    <Stack spacing={0} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {groupLabel}
      </Typography>

      <ToggleButtonGroup
        value={stringify(value)}
        exclusive
        onChange={(_, newValue) => setter(parseNullable(newValue))}
        fullWidth
      >
        {options.map((opt) => {
          return (
            <Tooltip
              key={opt.value}
              title={opt.tooltip || ''}
              placement="bottom"
            >
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {opt.label}
              </ToggleButton>
            </Tooltip>
          );
        })}
      </ToggleButtonGroup>
    </Stack>
  );

  return (
    <>
      {scope !== 'hour' &&
        renderToggleGroup(
          t('filter_labels.active_period'),
          activePeriodFilter,
          setActivePeriodFilter,
          [
            { label: 'Alle', value: '' },
            {
              label: t('filter_labels.active_period'),
              value: 'true',
              tooltip: <ActiveHoursTooltip openingHours={openingHours} />,
            },
            {
              label: t('filter_labels.inactive_period'),
              value: 'false',
              tooltip: (
                <ActiveHoursTooltip
                  openingHours={openingHours}
                  inverted={true}
                />
              ),
            },
          ],
        )}

      {scope !== 'duration' &&
        scope !== 'comparison' &&
        scope !== 'tree' &&
        renderToggleGroup(
          'Dage med aktivitet valgt i opsætning',
          daysFilter,
          setDaysFilter,
          [
            { label: 'Alle', value: '' },
            {
              label: <DayPills daysOfWeek={openingHours} />,
              value: 'true',
            },
            {
              label: <DayPills daysOfWeek={openingHours} invert size={14} />,
              value: 'false',
            },
          ],
        )}

      {renderToggleGroup(
        t('filter_labels.holidays'),
        holidaysFilter,
        setHolidaysFilter,
        [
          { label: 'Alle', value: '' },
          { label: 'Kun helligdage', value: 'true' },
          { label: 'Ingen helligdage', value: 'false' },
        ],
      )}
    </>
  );
};

const parseNullable = (val) => {
  if (val === 'true') return true;
  if (val === 'false') return false;
  return undefined;
};

const stringify = (val) => {
  if (val === true) return 'true';
  if (val === false) return 'false';
  return '';
};

export default EnergyAnalyzerFilterInput;
